import React, { useContext, useEffect, useState, useRef } from 'react';
import { Table, Button, Modal, Input, Row, Col, Pagination, AutoComplete, Spin } from 'antd';
import { SearchOutlined, BarcodeOutlined } from '@ant-design/icons';
import { LoginContext } from '../../../../context/loginContext';
import CDetalleArticuloEmpresa from './detalle-articulo-empresa.js';
import CCategoriaInicio from '../../publico/inicio/CCategoriaInicio.js';
import { useNavigate } from 'react-router-dom';

const isBarcode = (value) => /^\d{8,13}$/.test(value?.trim() || '');

const CListaArticuloAdministrador = () => {
  const navigate = useNavigate();
  const { user } = useContext(LoginContext);
  const [articulos, setArticulos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);
  const [visible, setVisible] = useState(false);
  const [busquedaTexto, setBusquedaTexto] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const searchTimeoutRef = useRef(null);
  const [categoriaFiltro, setCategoriaFiltro] = useState(null);
  const [pagination, setPagination] = useState({ current: 1, total: 0, pageSize: 10 });

  const empresaId = user?.emp_id;

  useEffect(() => {
    fetchArticulos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busquedaTexto, categoriaFiltro]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, []);

  const fetchAutocompleteSuggestions = async (value) => {
    if (!value || value.trim().length < 2 || !empresaId || !user?.token) {
      setAutocompleteOptions([]);
      return;
    }
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(async () => {
      setAutocompleteLoading(true);
      try {
        const queryParams = new URLSearchParams({ q: value.trim() });
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/articulos/empresa/${empresaId}/autocomplete?${queryParams.toString()}`,
          { headers: { Authorization: `Bearer ${user.token}` } }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!data.suggestions?.length) {
          setAutocompleteOptions([]);
          return;
        }
        const options = data.suggestions.map((item) => ({
          value: item.nombre,
          label: (
            <div>
              <div style={{ fontWeight: 500 }}>{item.nombre}</div>
              {item.marca && (
                <div style={{ fontSize: 12, color: '#888' }}>{item.marca}</div>
              )}
            </div>
          ),
          item
        }));
        setAutocompleteOptions(options);
      } catch (e) {
        console.error('Error al obtener sugerencias:', e);
        setAutocompleteOptions([]);
      } finally {
        setAutocompleteLoading(false);
      }
    }, 300);
  };

  const fetchArticulos = async (page = 1) => {
    if (!user?.emp_id || !user?.token) return;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page,
        limit: pagination.pageSize,
        categoria: categoriaFiltro || ''
      });
      if (busquedaTexto) {
        queryParams.append('busqueda_texto', busquedaTexto);
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/articulos/empresa/${user.emp_id}?${queryParams.toString()}`,
        {
          headers: { Authorization: `Bearer ${user.token}` }
        }
      );
      const data = await res.json();
      setArticulos(data.data ?? []);
      setPagination({
        current: data.pagination?.currentPage ?? 1,
        total: data.pagination?.total ?? 0,
        pageSize: data.pagination?.limit ?? pagination.pageSize
      });
    } catch (error) {
      console.error('Error al obtener artículos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleTableChange = (page) => {
    fetchArticulos(page);
  };

  const columns = [
    {
      title: 'Imagen',
      dataIndex: 'aem_imagen',
      key: 'aem_imagen',
      render: (imagen) =>
        imagen ? (
          <img
            src={imagen}
            alt="articulo"
            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
          />
        ) : null,
      width: 70, // para que no se expanda demasiado
    },
    {
      title: 'Nombre',
      dataIndex: 'aem_nombre',
      key: 'aem_nombre',
    },
    { title: 'SKU', dataIndex: 'aem_sku', key: 'aem_sku' },
    { title: 'Marca', dataIndex: 'aem_marca', key: 'aem_marca' },
    {
      title: 'Acciones',
      key: 'acciones',
      render: (_, record) => (
        <Button type="link" onClick={() => {
          setArticuloSeleccionado(record.aem_id);
          setVisible(true);
        }}>
          Ver/Editar
        </Button>
      )
    }
  ];

  return (
    <div style={{ padding: '2rem' }}>
      {/* Barra de filtros superior */}
      <div
        style={{
          backgroundColor: '#fff',
          borderBottom: '1px solid #f0f0f0',
          paddingBottom: '1rem',
          marginBottom: '1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '1rem'
        }}
      >
        <CCategoriaInicio
          onCategoriaSeleccionada={(catId) => {
            setPagination(p => ({ ...p, current: 1 }));
            setCategoriaFiltro(catId);
          }}
        />

        <div style={{ flex: 1, display: 'flex', justifyContent: 'center' }}>
          <AutoComplete
            style={{ maxWidth: 400, width: '100%' }}
            options={autocompleteOptions}
            onSearch={fetchAutocompleteSuggestions}
            onChange={(value) => {
              setSearchValue(value);
              if (value && value.trim().length >= 2) {
                fetchAutocompleteSuggestions(value);
              } else {
                setAutocompleteOptions([]);
              }
            }}
            onSelect={(value, option) => {
              const searchTerm = option?.item?.nombre || value;
              setSearchValue(searchTerm);
              setAutocompleteOptions([]);
              setPagination((p) => ({ ...p, current: 1 }));
              setBusquedaTexto(searchTerm.trim());
            }}
            value={searchValue}
            notFoundContent={autocompleteLoading ? <Spin size="small" /> : null}
            filterOption={false}
            dropdownMatchSelectWidth
          >
            <Input.Search
              placeholder="¿Qué estás buscando? (nombre, marca, código de barras...)"
              allowClear
              enterButton={
                <Button
                  type="primary"
                  icon={isBarcode(searchValue) ? <BarcodeOutlined /> : <SearchOutlined />}
                  style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
                />
              }
              onSearch={(value) => {
                setPagination((p) => ({ ...p, current: 1 }));
                const v = value && value.trim() ? value.trim() : '';
                setBusquedaTexto(v);
                setSearchValue(value || '');
              }}
              onChange={(e) => {
                const value = e.target.value;
                setSearchValue(value);
                if (!value || value.trim() === '') {
                  setBusquedaTexto('');
                  setAutocompleteOptions([]);
                } else if (value.trim().length >= 2) {
                  fetchAutocompleteSuggestions(value);
                }
              }}
            />
          </AutoComplete>
        </div>

        <div style={{ width: 'auto' }}></div>
      </div>

      <Row justify="space-between" align="middle" style={{ marginBottom: '1rem' }}>
        <Col><h2>Artículos de Empresa</h2></Col>
        <Col>
          <Button
            type="primary"
            onClick={() => navigate('/nuevo-articulo-empresa')}
            style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
          >
            Agregar Artículo
          </Button>
        </Col>
      </Row>

      <Table
        columns={columns}
        dataSource={articulos}
        rowKey="aem_id"
        loading={loading}
        pagination={false}
      />

      <Pagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={pagination.total}
        onChange={handleTableChange}
        style={{ marginTop: '1rem', textAlign: 'right' }}
      />

      <Modal
        open={visible}
        onCancel={() => setVisible(false)}
        footer={null}
        width={'90%'}
        style={{ top: 20 }}
        destroyOnClose
      >
        {articuloSeleccionado && (
          <CDetalleArticuloEmpresa
            articuloId={articuloSeleccionado}
            onSave={() => {
              setVisible(false);
              fetchArticulos(pagination.current);
            }}
          />
        )}
      </Modal>
    </div>
  );
};

export default CListaArticuloAdministrador;
